import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import FloatingShapes from '@/components/FloatingShapes';
import DataMatrixVideo from '@/components/DataMatrixVideo';

const PROBLEMS = [
  {
    icon: 'ri-book-open-line',
    titleKey: 'solutions.challenges.p1.title',
    descKey: 'solutions.challenges.p1.desc',
    accent: 'accent',
  },
  {
    icon: 'ri-file-list-3-line',
    titleKey: 'solutions.challenges.p2.title',
    descKey: 'solutions.challenges.p2.desc',
    accent: 'secondary',
  },
  {
    icon: 'ri-exchange-line',
    titleKey: 'solutions.challenges.p3.title',
    descKey: 'solutions.challenges.p3.desc',
    accent: 'accent',
  },
  {
    icon: 'ri-database-2-line',
    titleKey: 'solutions.challenges.p4.title',
    descKey: 'solutions.challenges.p4.desc',
    accent: 'secondary',
  },
  {
    icon: 'ri-tools-line',
    titleKey: 'solutions.challenges.p5.title',
    descKey: 'solutions.challenges.p5.desc',
    accent: 'accent',
  },
  {
    icon: 'ri-question-line',
    titleKey: 'solutions.challenges.p6.title',
    descKey: 'solutions.challenges.p6.desc',
    accent: 'secondary',
  },
];

export default function BusinessChallengesSection() {
  const sectionRef = useRef<HTMLElement>(null);
  const [isVisible, setIsVisible] = useState(false);
  const { t } = useTranslation();

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.1 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section ref={sectionRef} className="relative py-12 md:py-16 bg-background-50 overflow-hidden">
      {/* Floating shapes */}
      <FloatingShapes count={16} color="secondary" />
      <div className="absolute inset-0 pointer-events-none">
        <FloatingShapes count={12} color="accent" />
      </div>

      {/* Gradient orbs */}
      <div className="absolute top-0 left-[20%] w-80 h-80 bg-secondary-500/6 rounded-full blur-3xl animate-[morphFloat_7s_ease-in-out_infinite]" />
      <div className="absolute bottom-0 right-[15%] w-96 h-96 bg-accent-500/5 rounded-full blur-3xl animate-[morphPulse_8s_ease-in-out_infinite_1s]" />

      {/* Decorative moving elements */}
      <div className="absolute top-40 right-[10%] w-10 h-10 border border-accent-300/20 rounded-lg -rotate-12 animate-[morphFloat_6s_ease-in-out_infinite]" />
      <div className="absolute bottom-20 left-[15%] w-6 h-6 border border-secondary-300/20 rounded-full animate-[morphPulse_5s_ease-in-out_infinite_2s]" />

      <div className="relative w-full px-4 md:px-8 lg:px-12">
        <div className="max-w-7xl mx-auto">
          {/* Section header */}
          <div
            className={`text-center mb-16 transition-all duration-1000 ${
              isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
            }`}
          >
            <span className="inline-block px-3 py-1 bg-secondary-100/80 rounded-full text-[10px] font-mono font-medium text-secondary-700 uppercase tracking-widest mb-4">
              {t('solutions.challenges.badge')}
            </span>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-heading font-semibold text-foreground-950 leading-tight tracking-tight max-w-3xl mx-auto">
              {t('solutions.challenges.title1')}{' '}
              <span className="text-secondary-500">{t('solutions.challenges.title2')}</span>
            </h2>
            <p className="mt-4 text-base md:text-lg text-foreground-600 leading-relaxed max-w-2xl mx-auto">
              {t('solutions.challenges.paragraph')}
            </p>
          </div>

          <div className="grid lg:grid-cols-3 gap-6 lg:gap-8">
            {/* Left: Problem cards */}
            <div className="lg:col-span-2 grid sm:grid-cols-2 gap-4 md:gap-5">
              {PROBLEMS.map((problem, i) => (
                <div
                  key={problem.titleKey}
                  className={`group relative p-5 md:p-6 rounded-xl border border-background-200/80 bg-background-50/80 backdrop-blur-sm transition-all duration-500 hover:shadow-lg hover:shadow-background-200/50 hover:-translate-y-1 cursor-default ${
                    isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
                  }`}
                  style={{ transitionDelay: `${0.1 + i * 0.08}s` }}
                >
                  {/* Accent line on hover */}
                  <div
                    className={`absolute top-0 left-0 right-0 h-0.5 rounded-t-xl transition-all duration-300 scale-x-0 group-hover:scale-x-100 origin-left ${
                      problem.accent === 'accent' ? 'bg-accent-400' : 'bg-secondary-400'
                    }`}
                  />

                  {/* Floating corner icon on hover */}
                  <div
                    className={`absolute top-3 right-3 w-8 h-8 rounded-full flex items-center justify-center transition-all duration-300 opacity-0 group-hover:opacity-100 scale-50 group-hover:scale-100 ${
                      problem.accent === 'accent' ? 'bg-accent-100' : 'bg-secondary-100'
                    }`}
                  >
                    <i className={`${problem.icon} text-sm ${problem.accent === 'accent' ? 'text-accent-500' : 'text-secondary-500'}`} />
                  </div>

                  <div className="flex items-start gap-3 mb-3">
                    <div
                      className={`w-10 h-10 rounded-lg flex items-center justify-center shrink-0 transition-transform duration-300 group-hover:scale-110 ${
                        problem.accent === 'accent'
                          ? 'bg-accent-100 text-accent-600'
                          : 'bg-secondary-100 text-secondary-600'
                      }`}
                    >
                      <i className={`${problem.icon} text-lg`} />
                    </div>
                    <h3 className="text-base font-heading font-semibold text-foreground-800 pt-1">
                      {t(problem.titleKey)}
                    </h3>
                  </div>
                  <p className="text-sm text-foreground-600 leading-relaxed pl-[52px]">
                    {t(problem.descKey)}
                  </p>
                </div>
              ))}
            </div>

            {/* Right: Image + CTA */}
            <div
              className={`flex flex-col gap-6 transition-all duration-1000 ${
                isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
              }`}
              style={{ transitionDelay: '0.4s' }}
            >
              {/* Animated tech visualization */}
              <div className="relative rounded-xl overflow-hidden group flex-1 min-h-[300px]">
                <div className="absolute inset-0 bg-primary-500/5">
                  <DataMatrixVideo color="accent" density={1} />
                  <div className="absolute inset-0 bg-gradient-to-br from-accent-500/10 via-transparent to-secondary-500/10 pointer-events-none" />
                </div>
                <div className="absolute inset-0 bg-gradient-to-t from-primary-500/30 via-primary-500/5 to-transparent pointer-events-none" />

                {/* Floating stat badges */}
                <div className="absolute bottom-4 left-4 right-4 flex flex-wrap gap-2">
                  {[
                    { label: t('solutions.challenges.before.label'), value: t('solutions.challenges.before.value') },
                    { label: t('solutions.challenges.after.label'), value: t('solutions.challenges.after.value') },
                  ].map((stat) => (
                    <div
                      key={stat.label}
                      className="px-3 py-1.5 bg-background-50/90 backdrop-blur-sm rounded-md border border-background-200/60"
                    >
                      <span className="text-[10px] font-mono text-foreground-500 uppercase tracking-wider block">{stat.label}</span>
                      <span className="text-sm font-heading font-semibold text-foreground-800">{stat.value}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* CTA card */}
              <div className="p-6 rounded-xl border border-accent-200/60 bg-accent-50/30">
                <p className="text-sm font-heading font-medium text-foreground-800 mb-4">
                  {t('solutions.challenges.cta')}
                </p>
                <Link
                  to="/shape-your-project"
                  className="inline-flex items-center gap-2 w-full justify-center px-5 py-3 bg-primary-500 text-background-50 text-sm font-semibold rounded-md hover:bg-primary-600 transition-all hover:scale-[1.02]"
                >
                  {t('cta.shapeYourProject')}
                  <i className="ri-arrow-right-line" />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}